import { create } from 'zustand'
import { supabase } from '../lib/supabaseClient'
import { createUserProfile } from '../utils/simpleSetup'

export const useAuthStore = create((set, get) => ({
  user: null,
  profile: null,
  loading: true,
  error: null,

  // Initialize auth state
  initializeAuth: async () => {
    try {
      set({ loading: true })
      
      // Get current session
      const { data: { session } } = await supabase.auth.getSession()
      
      if (session?.user) {
        // Try to get user profile, but don't fail if table doesn't exist
        try {
          const { data: profile, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single()
          
          if (error) {
            console.warn('Profile not found, creating new profile:', error.message)
            // Try to create profile in database
            const profileData = await createUserProfile(session.user.id, {
              email: session.user.email,
              name: session.user.user_metadata?.name || session.user.email?.split('@')[0],
              role: session.user.user_metadata?.role || 'client'
            })
            
            set({ user: session.user, profile: profileData, loading: false })
          } else {
            console.log('✅ Profile fetched successfully:', profile)
            set({ user: session.user, profile, loading: false })
          }
        } catch (profileError) {
          console.warn('Error fetching profile:', profileError)
          // Create a default profile object
          const defaultProfile = {
            id: session.user.id,
            name: session.user.user_metadata?.name || session.user.email,
            email: session.user.email,
            role: 'client'
          }
          set({ user: session.user, profile: defaultProfile, loading: false })
        }
      } else {
        set({ user: null, profile: null, loading: false })
      }

      // Listen for auth changes
      supabase.auth.onAuthStateChange(async (event, session) => {
        if (session?.user) {
          try {
            const { data: profile } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', session.user.id)
              .single()
            
            if (profile) {
              set({ user: session.user, profile })
            } else {
              // Create default profile
              const defaultProfile = {
                id: session.user.id,
                name: session.user.user_metadata?.name || session.user.email,
                email: session.user.email,
                role: 'client'
              }
              set({ user: session.user, profile: defaultProfile })
            }
          } catch (error) {
            console.warn('Error fetching profile on auth change:', error)
            const defaultProfile = {
              id: session.user.id,
              name: session.user.user_metadata?.name || session.user.email,
              email: session.user.email,
              role: 'client'
            }
            set({ user: session.user, profile: defaultProfile })
          }
        } else {
          set({ user: null, profile: null })
        }
        set({ loading: false })
      })
    } catch (error) {
      console.error('Auth initialization error:', error)
      set({ error: error.message, loading: false })
    }
  },

  // Sign up
  signUp: async (email, password, userData) => {
    try {
      set({ loading: true, error: null })
      console.log('🚀 Starting signup process for:', email)
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData
        }
      })
      
      if (error) {
        console.error('❌ Auth signup error:', error)
        throw error
      }
      
      console.log('✅ Auth signup successful:', data.user?.id)
      
      // Create profile after successful signup
      if (data.user) {
        console.log('📝 Creating profile for user:', data.user.id)
        
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .insert({
            id: data.user.id,
            name: userData.name,
            email: email,
            role: userData.role || 'client',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select()
          .single()
        
        if (profileError) {
          console.error('❌ Profile creation error:', profileError)
          // Don't throw error, just log it - user can still sign up
        } else {
          console.log('✅ Profile created successfully:', profileData)
        }
      }
      
      set({ loading: false })
      return { data, error: null }
    } catch (error) {
      console.error('❌ Signup error:', error)
      set({ error: error.message, loading: false })
      return { data: null, error }
    }
  },

  // Sign in
  signIn: async (email, password) => {
    try {
      set({ loading: true, error: null })
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })
      
      if (error) throw error
      
      if (data.user) {
        // Try to get profile, but create default if not found
        try {
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', data.user.id)
            .single()
          
          if (profileError || !profile) {
            console.warn('Profile not found, fetching from database')
            // Try to get profile from database using createUserProfile
            const profileData = await createUserProfile(data.user.id, {
              email: data.user.email,
              name: data.user.user_metadata?.name || data.user.email?.split('@')[0],
              role: data.user.user_metadata?.role || 'client'
            })
            set({ user: data.user, profile: profileData, loading: false })
          } else {
            console.log('✅ Profile found during sign in:', profile)
            set({ user: data.user, profile, loading: false })
          }
        } catch (profileError) {
          console.warn('Error fetching profile on sign in:', profileError)
          const defaultProfile = {
            id: data.user.id,
            name: data.user.user_metadata?.name || data.user.email,
            email: data.user.email,
            role: 'client'
          }
          set({ user: data.user, profile: defaultProfile, loading: false })
        }
      }
      
      return { data, error: null }
    } catch (error) {
      set({ error: error.message, loading: false })
      return { data: null, error }
    }
  },

  // Sign out
  signOut: async () => {
    try {
      set({ loading: true })
      
      // Clear local state first
      set({ user: null, profile: null, loading: false })
      
      // Try to sign out from Supabase, but don't fail if it doesn't work
      try {
        const { error } = await supabase.auth.signOut()
        if (error) {
          console.warn('Supabase sign out error:', error)
        }
      } catch (signOutError) {
        console.warn('Supabase sign out failed:', signOutError)
      }
      
      return { error: null }
    } catch (error) {
      console.warn('Sign out error:', error)
      set({ user: null, profile: null, loading: false })
      return { error }
    }
  },

  // Reset password
  resetPassword: async (email) => {
    try {
      set({ loading: true, error: null })
      const { error } = await supabase.auth.resetPasswordForEmail(email)
      
      if (error) throw error
      
      set({ loading: false })
      return { error: null }
    } catch (error) {
      set({ error: error.message, loading: false })
      return { error }
    }
  },

  // Update profile
  updateProfile: async (updates) => {
    try {
      const { user } = get()
      if (!user) throw new Error('No user logged in')
      
      set({ loading: true, error: null })
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single()
      
      if (error) throw error
      
      set({ profile: data, loading: false })
      return { data, error: null }
    } catch (error) {
      set({ error: error.message, loading: false })
      return { data: null, error }
    }
  },

  // Clear error
  clearError: () => set({ error: null }),

  // Check if user has role
  hasRole: (role) => {
    const { profile } = get()
    return profile?.role === role
  },

  // Check if user has any of the roles
  hasAnyRole: (roles) => {
    const { profile } = get()
    return roles.includes(profile?.role)
  }
}))
